import {
  FinalFee,
  FinalFeeType,
  FinalProduct,
  FinanceResponseData,
  Insurance,
  InsuranceType,
  OtherCharge,
} from '@upstart/harmony/lib/legos/financingTypes';
import { ProductCategory } from '@upstart/harmony/lib/legos/penTypes';
import { TradeIn } from '@upstart/harmony/lib/legos/dealTypes';

export enum FormElementEnum {
  FORM_TITLE = 'formTitle',
  SECTION = 'section',
  SUBSECTION = 'subsection',
  FEE_INPUT = 'feeInput',
  PRICE_INPUT = 'priceInput',
  STRING_INPUT = 'stringInput',
  INTEGER_INPUT = 'integerInput',
  CHARGE_TABLE = 'chargeTable',
  FEE_TABLE = 'feeTable',
  PRODUCT_TABLE = 'productTable',
  INSURANCE_TABLE = 'insuranceTable',
  TRADE_IN_INPUT = 'tradeInInput',
  FEE_DROPDOWN = 'feeDropdown',
  LINE_ITEM = 'lineItem',
}

export type OtherChargeFields =
  | 'otherNonTaxableCharges'
  | 'otherDownPayments'
  | 'otherTaxableCharges'
  | 'carDefects';

export type ColumnType = { title?: string; width: string };

export type FeeColumnType = { fieldName: keyof FinalFee } & ColumnType;
export type ProductColumnType = { fieldName: keyof FinalProduct } & ColumnType;
export type InsuranceColumnType = { fieldName: keyof Insurance } & ColumnType;
export type OtherChargeColumnType = {
  fieldName: keyof OtherCharge;
} & ColumnType;

export type SumValueType = {
  responseDataFields?: (keyof FinanceResponseData)[];
  feeTypes?: FinalFeeType[];
  productCategories?: (ProductCategory | undefined)[];
  otherCharges?: OtherChargeFields[];
  insuranceTypes?: InsuranceType[];
  downPaymentPayoff?: boolean;
  downPaymentWithMinZero?: boolean;
  subtractDownPaymentWithMinZero?: boolean;
};

export type FormTitleType = {
  type: FormElementEnum.FORM_TITLE;
  title?: string;
};

export type BaseFormElementType = {
  title?: string;
  subtitle?: string;
  required?: boolean;
};

export type LineItemType = {
  type: FormElementEnum.LINE_ITEM;
  value?: string;
  sumValue?: SumValueType;
  responseDataField?: keyof FinanceResponseData;
} & BaseFormElementType;

export type FormSectionType = {
  type: FormElementEnum.SECTION;
  value?: string;
  sumValue?: SumValueType;
  sectionElements?: FormElementType[];
  options?: { onlyShowForUsedCar?: boolean };
} & BaseFormElementType;

export type FormSubsectionType = {
  type: FormElementEnum.SUBSECTION;
  value?: string;
  sumValue?: SumValueType;
  subSectionElements?: FormElementType[];
} & BaseFormElementType;

export type FeeInputType = {
  type: FormElementEnum.FEE_INPUT;
  feeType: FinalFeeType;
} & BaseFormElementType;

export type PriceInputType = {
  type: FormElementEnum.PRICE_INPUT;
  responseDataField: keyof FinanceResponseData;
} & BaseFormElementType;

export type StringInputType = {
  type: FormElementEnum.STRING_INPUT;
  responseDataField: keyof FinanceResponseData;
} & BaseFormElementType;

export type IntegerInputType = {
  type: FormElementEnum.INTEGER_INPUT;
  responseDataField: keyof FinanceResponseData;
} & BaseFormElementType;

export type ChargeTableType = {
  type: FormElementEnum.CHARGE_TABLE;
  responseDataField: OtherChargeFields;
  columns: OtherChargeColumnType[];
  displayTotalValue: boolean;
  defaultDescriptions?: string[];
  options?: { hideAmountInputs?: boolean };
} & BaseFormElementType;

export type FeeTableType = {
  type: FormElementEnum.FEE_TABLE;
  feeType: FinalFeeType;
  columns: FeeColumnType[];
  displayTotalValue?: boolean;
} & BaseFormElementType;

export type ProductTableType = {
  type: FormElementEnum.PRODUCT_TABLE;
  productCategory: ProductCategory;
  columns: ProductColumnType[];
  displayTotalValue?: boolean;
} & BaseFormElementType;

export type InsuranceTableType = {
  type: FormElementEnum.INSURANCE_TABLE;
  insuranceTypes: InsuranceType[];
  columns: InsuranceColumnType[];
  displayTotalValue?: boolean;
} & BaseFormElementType;

export type TradeInItemType = {
  type: FormElementEnum.TRADE_IN_INPUT;
  tradeInField: keyof TradeIn | 'netTrade';
  value?: string;
  sumValue?: SumValueType;
} & BaseFormElementType;

export type FeeDropdownType = {
  type: FormElementEnum.FEE_DROPDOWN;
  feeTypes: FinalFeeType[];
  value?: string | number;
} & BaseFormElementType;

export type FormElementType =
  | FormTitleType
  | LineItemType
  | FormSectionType
  | FormSubsectionType
  | FeeInputType
  | PriceInputType
  | StringInputType
  | IntegerInputType
  | ChargeTableType
  | FeeTableType
  | ProductTableType
  | InsuranceTableType
  | TradeInItemType
  | FeeDropdownType;

export type RicMetadata = {
  feeTypeList: FinalFeeType[];
  numDefects?: number;
  numInsuranceCreditDisability?: number;
  numInsuranceCreditLife?: number;
  numGapProducts?: number;
  numOtherDownpaymentElements?: number;
  numOtherFees?: number;
  numGovernmentFeesTax?: number;
  numOtherNonTaxableCharges?: number;
  numOtherTaxableCharges?: number;
  numServiceContracts?: number;
  numSurfaceProtectionProducts?: number;
  numTheftDeterrentDevices?: number;
  numTradeIns?: number;
  rebateAsOtherCharge?: boolean;
  downPaymentAsOtherCharge?: boolean;
  excludeFromFeesTotal?: FinalFeeType[];
};

export type RicFormSchema = {
  metadata?: RicMetadata;
  elements: FormElementType[];
};
